'use client';

import * as React from 'react';

/**
 * Merges refs into a single memoized callback ref (MUI-compatible).
 */
export default function useForkRef<Instance>(
  ...refs: Array<React.Ref<Instance> | undefined>
): React.RefCallback<Instance> | null {
  const cleanupRef = React.useRef<(() => void) | undefined>(undefined);
  const refEffect = React.useCallback(
    (instance: Instance | null) => {
      const cleanups = refs.map((ref) => {
        if (ref == null) {
          return null;
        }
        if (typeof ref === 'function') {
          const refCallback = ref as (inst: Instance | null) => void | (() => void);
          const refCleanup = refCallback(instance);
          return typeof refCleanup === 'function'
            ? refCleanup
            : () => {
                refCallback(null);
              };
        }
        (ref as React.MutableRefObject<Instance | null>).current = instance;
        return () => {
          (ref as React.MutableRefObject<Instance | null>).current = null;
        };
      });
      return () => {
        cleanups.forEach((refCleanup) => refCleanup?.());
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo -- ref list matches MUI
    refs
  );

  return React.useMemo(() => {
    if (refs.every((ref) => ref == null)) {
      return null;
    }
    return (value: Instance | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
      if (value != null) {
        cleanupRef.current = refEffect(value);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo -- ref list matches MUI
  }, refs);
}
